import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Divider,
  Stack,
  InputAdornment,
  Link,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import type {
  Customer,
  InvoiceCreateInput,
  Order,
  PaymentTerms,
  ShipMethods,
} from '@repo/api-contract';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  gridItemProps,
  textFieldProps,
  textFieldSelectProps,
} from '../../constants/form';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProps } from 'formik';
import {
  getFormikField,
  onNumberFieldBlur,
  onNumberFieldFocus,
} from '../../../../util/fornik-helpers';
import { useTranslation } from 'react-i18next';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import SelectSearchCustomer from '../../../utils/SelectSearchCustomer';
import {
  closeModal,
  openInvoiceModal,
  InvoiceModalMode,
  setAddresses,
  CustomerDeliveryAddressWithId,
  setCustomer,
  setOrderField,
} from '../../../../state/invoiceModalSlice';
import { openSearchCustomerModal } from '../../../../state/customerSearchModalSlice';
import { openNewCustomerModal } from '../../../../state/customerModalSlice';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import theme from '../../../../util/theme';
import { client } from '../../../../util';
import { v4 as uuidv4 } from 'uuid';
import SearchOrder from '../../../utils/SearchOrder';
import { typographyWrap } from './constants';
import { openSearchOrderModal } from '../../../../state/orderSearchModalSlice';
import React from 'react';

export default function InvoiceDetails({
  formik,
  paymentTerms,
  shipMethods,
  viewMode,
}: {
  formik: FormikProps<InvoiceCreateInput>;
  paymentTerms: PaymentTerms[];
  shipMethods: ShipMethods[];
  viewMode: boolean;
}): JSX.Element {
  const { t: tGC } = useTranslation([], { keyPrefix: 'team.Global.Common' });
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tAddI } = useTranslation([], {
    keyPrefix: 'team.Modals.Invoices.createAndEdit.Add Invoice',
  });

  const dispatch = useDispatch();

  const { mode, addresses, orderFieldCode, orderFieldColor } = useSelector(
    ({ invoiceModalSlice }) => invoiceModalSlice,
  );

  const invoiceDate =
    formik.values.invoiceDate != 0
      ? dayjs.unix(formik.values.invoiceDate)
      : null;

  const genUUID = () => {
    let uuid = uuidv4();
    return uuid;
  };

  const handleChangeInvoiceDate = (newValue: Dayjs | null) => {
    // Convert the new date value to Unix time (seconds since the Unix Epoch)
    const unixTime = newValue ? newValue.unix() : null;
    // Update Formik's state with Unix time
    formik.setFieldValue('invoiceDate', unixTime);
  };

  const estimatedShipDate =
    formik.values.estimatedShipDate != 0
      ? dayjs.unix(formik.values.estimatedShipDate)
      : null;

  const handleChangeEstShipDate = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.unix() : null;
    formik.setFieldValue('estimatedShipDate', unixTime);
  };

  const deadline =
    formik.values.deadline != 0 ? dayjs.unix(formik.values.deadline) : null;

  const handleChangeDeadline = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.unix() : null;
    formik.setFieldValue('deadline', unixTime);
  };

  const onImportCustomer = (selectedCustomers: Customer[]) => {
    const customer = selectedCustomers.at(0); // This safely returns undefined if empty
    if (!customer) return;

    dispatch(openInvoiceModal());
    handleChangeCustomer(customer);
  };

  const onCancelCustomer = () => {
    dispatch(openInvoiceModal());
  };

  const openBrowseCustomerModal = () => {
    dispatch(closeModal());
    dispatch(
      openSearchCustomerModal({
        onImport: onImportCustomer,
        onCancel: onCancelCustomer,
        multiSelect: false,
      }),
    );
  };

  const onImportOrder = (selectedOrders: Order[]) => {
    const order = selectedOrders.at(0); // This safely returns undefined if empty
    if (!order) return;

    dispatch(openInvoiceModal());
    // Setting field and triggereing fetching function
    dispatch(setOrderField({ orderFieldCode: order.code }));
    SearchOrderFetch(order);
  };

  const onCancelOrder = () => {
    dispatch(openInvoiceModal());
  };

  const openBrowseOrderModal = () => {
    dispatch(closeModal());
    dispatch(
      openSearchOrderModal({
        onImport: onImportOrder,
        onCancel: onCancelOrder,
        multiSelect: false,
      }),
    );
  };

  const newCustomerCallBack = async (
    customer: Customer | undefined,
  ): Promise<void> => {
    dispatch(openInvoiceModal());
    if (!customer) return;

    handleChangeCustomer(customer);
  };

  const openCreateCustomerModal = () => {
    dispatch(closeModal());
    dispatch(openNewCustomerModal({ createCallback: newCustomerCallBack }));
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('code', event.target.value);
  };

  type ChangeCustomerOpts = {
    setDefaults?: boolean; // default true
  };

  const buildCustomerAddresses = (customer: Customer) => {
    let defaultAddress: CustomerDeliveryAddressWithId | undefined = undefined;
    const addresses: CustomerDeliveryAddressWithId[] = [];

    // Billing address
    if (customer.billing) {
      const id = genUUID();
      const obj = {
        ...customer.billing,
        id,
        type: 'billing' as const,
      };
      if (!defaultAddress) defaultAddress = obj;
      addresses.push(obj);
    }

    // All Delivery addresses
    if (customer.delivery) {
      customer.delivery.forEach((addr) => {
        const id = genUUID();
        const obj = {
          ...addr,
          id,
          type: 'delivery' as const,
        };
        if (!defaultAddress) defaultAddress = obj;
        addresses.push(obj);
      });
    }

    // Customer Details to Address
    if (customer.details && customer.details.address) {
      const id = genUUID();
      const { website, ...detailsWithoutWebsite } = customer.details as any;

      const obj = {
        ...detailsWithoutWebsite,
        id,
        type: 'customer' as const,
      };
      if (!defaultAddress) defaultAddress = obj;
      addresses.push(obj);
    }

    return { addresses, defaultAddress };
  };

  const handleChangeCustomer = (
    customer: Customer | null,
    opts?: ChangeCustomerOpts,
  ) => {
    const setDefaults = opts?.setDefaults ?? true;

    if (customer != null) {
      formik.setFieldValue('customer.code', customer.code);
      formik.setFieldValue('customer.CustomerId', customer.CustomerId);
      formik.setFieldValue(
        'customer.companyName',
        customer.details.companyName,
      );

      const { addresses, defaultAddress } = buildCustomerAddresses(customer);
      dispatch(setAddresses(addresses));

      if (setDefaults && defaultAddress) {
        formik.setFieldValue('invoiceShipTo', defaultAddress);
        formik.setFieldValue('poShipTo', defaultAddress);
      }

      if (customer.payment?.taxCode) {
        formik.setFieldValue('taxCode', customer.payment.taxCode);
      }

      dispatch(setCustomer(customer));
    } else {
      formik.setFieldValue('customer.code', '');
      formik.setFieldValue('customer.CustomerId', '');
      formik.setFieldValue('customer.companyName', '');
      formik.setFieldValue('invoiceShipTo', '');
      formik.setFieldValue('poShipTo', '');
      dispatch(setCustomer(undefined));
      dispatch(setAddresses([]));
    }
  };

  // ------------------------------------------------------------
  // Edit mode: customer already set -> fetch addresses but DON'T set defaults

  const customerId = formik.values.customer.CustomerId;
  const lastFetchedCustomerIdRef = React.useRef<string>('');

  React.useEffect(() => {
    const run = async () => {
      if (!customerId) return;
      if (lastFetchedCustomerIdRef.current === customerId) return;

      lastFetchedCustomerIdRef.current = customerId;

      const resp = await client.customerById.query(customerId);

      // Populate customer fields + addresses, but don't overwrite invoiceShipTo/poShipTo
      handleChangeCustomer(resp, { setDefaults: false });

      // Rebuild addresses locally so we can append 2 "selected" addresses from formik
      const { addresses: builtAddresses } = buildCustomerAddresses(resp);

      const appended: CustomerDeliveryAddressWithId[] = [...builtAddresses];

      // If invoiceShipTo exists (edit invoice), add it as an option with its own UUID
      const currentInvoiceShipTo = formik.values.invoiceShipTo as any;
      if (currentInvoiceShipTo && typeof currentInvoiceShipTo === 'object') {
        const invoiceShipToWithId: CustomerDeliveryAddressWithId = {
          ...currentInvoiceShipTo,
          id: genUUID(),
          type: (currentInvoiceShipTo.type ?? 'invoice') as any, // type is only for your tooltip
        };

        appended.push(invoiceShipToWithId);
        formik.setFieldValue('invoiceShipTo', invoiceShipToWithId);
      }

      // If poShipTo exists (edit invoice), add it as an option with its own UUID
      const currentPoShipTo = formik.values.poShipTo as any;
      if (currentPoShipTo && typeof currentPoShipTo === 'object') {
        const poShipToWithId: CustomerDeliveryAddressWithId = {
          ...currentPoShipTo,
          id: genUUID(),
          type: (currentPoShipTo.type ?? 'po') as any,
        };

        appended.push(poShipToWithId);
        formik.setFieldValue('poShipTo', poShipToWithId);
      }

      dispatch(setAddresses(appended));
    };

    run();
  }, [customerId]);

  const SearchOrderFetch = async (order: Order | null) => {
    if (order) {
      dispatch(setOrderField({ orderFieldColor: 'success' }));
      formik.setFieldValue('orderId', order?.OrderId);
      formik.setFieldValue('customerDeposit', order?.customerDeposit);
      formik.setFieldValue('depositReference', order?.depositReference ?? '');
      if (order.customer.CustomerId != '') {
        const resp = await client.customerById.query(order.customer.CustomerId);
        handleChangeCustomer(resp);
      }
      formik.setFieldValue('estimatedShipDate', order.estimatedShipDate);
      formik.setFieldValue('shipMethod', order.shipMethod);
      formik.setFieldValue('deadline', order.deadline);
      formik.setFieldValue('paymentTerms', order.paymentTerms);
      formik.setFieldValue('items', order.items);
    } else {
      dispatch(setOrderField({ orderFieldColor: 'error' }));
      dispatch(
        pushToast({
          message: `${tAddI('Order not found')}`,
          type: 'error',
          duration: 7000,
        }),
      );
    }
  };
  const HandleChangeOrder = (e: React.ChangeEvent<HTMLInputElement>) => {
    // We check if user entered empty field then color of field should be default
    if (e.target.value == '') {
      dispatch(
        setOrderField({
          orderFieldCode: e.target.value,
          orderFieldColor: 'primary',
        }),
      );
    } else {
      dispatch(setOrderField({ orderFieldCode: e.target.value }));
    }
  };

  const getAddressTitle = (
    addressType: string,
  ): { title: string; letter: string } => {
    switch (addressType) {
      case 'billing':
        return { title: tAddI('Billing address of customer'), letter: 'B' };
      case 'delivery':
        return { title: tAddI('Delivery address of customer'), letter: 'D' };
      case 'customer':
        return { title: tAddI('Customer address'), letter: 'C' };
      default:
        return { title: tAddI('Unknown address type'), letter: '?' };
    }
  };

  // Reconstructing a partial Customer object from the three fields in formik.
  const currentCustomer = formik.values.customer.companyName
    ? ({
        code: formik.values.customer.code,
        CustomerId: formik.values.customer.CustomerId,
        details: {
          companyName: formik.values.customer.companyName,
        },
      } as Customer)
    : null; // If companyName doesn't exist, there's no selected customer

  const selectedPaymentTerm = getFormikField(formik, 'paymentTerms').value;
  const selectedShipMethod = getFormikField(formik, 'shipMethod').value;

  return (
    <>
      <Typography color="primary">{tAddI('INVOICE DETAILS')}</Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <Box maxWidth={'300px'} display="flex" gap={2} width="100%">
              <TextField
                variant="filled"
                disabled={viewMode || mode == InvoiceModalMode.EDIT}
                label={tAddI('Invoice No.')}
                name="code"
                type="number"
                onFocus={onNumberFieldFocus(formik, 'code')}
                onBlur={onNumberFieldBlur(formik, 'code')}
                error={Boolean(getFormikField(formik, 'code').error)}
                helperText={getFormikField(formik, 'code').error}
                value={getFormikField(formik, 'code').value}
                onChange={handleCodeChange}
              />
              <Box>
                <SearchOrder
                  label={tAddI('Import Order#')}
                  disabled={viewMode}
                  onFetch={SearchOrderFetch}
                  color={orderFieldColor}
                  value={orderFieldCode}
                  onChange={HandleChangeOrder}
                />
                <Box sx={{ justifyContent: 'start', display: 'flex' }}>
                  <Link
                    underline="always"
                    sx={{ cursor: 'pointer' }}
                    onClick={openBrowseOrderModal}
                  >
                    {tAddI('Browse Order')}
                  </Link>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddI('Invoice Date')}
                {...textFieldProps}
                value={invoiceDate}
                onChange={handleChangeInvoiceDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(getFormikField(formik, 'invoiceDate').error),
                    helperText: getFormikField(formik, 'invoiceDate').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid {...gridItemProps}>
            <Stack {...textFieldProps} gap={1}>
              <SelectSearchCustomer
                label={tGC('Customer')}
                onChange={handleChangeCustomer}
                disabled={viewMode}
                value={currentCustomer}
                error={Boolean(
                  getFormikField(formik, 'customer.companyName').error,
                )}
                helperText={
                  getFormikField(formik, 'customer.companyName').error
                }
              />
              <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
                <Link
                  underline="always"
                  sx={{ cursor: 'pointer' }}
                  onClick={openBrowseCustomerModal}
                >
                  {tAddI('Browse Customer')}
                </Link>
                <Link
                  underline="always"
                  sx={{ cursor: 'pointer' }}
                  onClick={openCreateCustomerModal}
                >
                  {tGB('Add Customer')}
                </Link>
              </Box>
            </Stack>
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tAddI('Billing Address')}
              select
              name="invoiceShipTo"
              disabled={
                viewMode ||
                getFormikField(formik, 'customer.CustomerId').value == ''
              }
              error={Boolean(getFormikField(formik, 'invoiceShipTo').error)}
              helperText={getFormikField(formik, 'invoiceShipTo').error}
              value={getFormikField(formik, 'invoiceShipTo').value.id ?? ''}
              onChange={(event) => {
                const selectedAddress = addresses.find(
                  (cA) => cA.id === event.target.value,
                );
                formik.setFieldValue('invoiceShipTo', selectedAddress);
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {addresses.map((cA) => {
                const { title, letter } = getAddressTitle(cA.type);

                const parts = [cA.address, cA.city, cA.state, cA.zipCode]
                  .map((v) => (v ?? '').trim())
                  .filter(Boolean);

                // If all are empty -> don't render this address option
                if (parts.length === 0) return null;

                const addressLabel = parts.join(', ');

                return (
                  <MenuItem key={cA.id} value={cA.id}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      alignItems="center"
                    >
                      <Typography {...typographyWrap}>
                        {addressLabel}
                      </Typography>

                      <Tooltip placement="right" title={title} arrow>
                        <Typography
                          variant="body2"
                          sx={{ ml: 1, cursor: 'help', fontWeight: 'bold' }}
                        >
                          {letter}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tAddI('Sales Order Ship To')}
              select
              name="poShipTo"
              disabled={
                viewMode ||
                getFormikField(formik, 'customer.CustomerId').value == ''
              }
              error={Boolean(getFormikField(formik, 'poShipTo').error)}
              helperText={getFormikField(formik, 'poShipTo').error}
              value={getFormikField(formik, 'poShipTo').value.id ?? ''}
              onChange={(event) => {
                const selectedAddress = addresses.find(
                  (cA) => cA.id === event.target.value,
                );
                formik.setFieldValue('poShipTo', selectedAddress);
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {addresses.map((cA) => {
                const { title, letter } = getAddressTitle(cA.type);

                const parts = [cA.address, cA.city, cA.state, cA.zipCode]
                  .map((v) => (v ?? '').trim())
                  .filter(Boolean);

                // If all are empty -> don't render this address option
                if (parts.length === 0) return null;

                const addressLabel = parts.join(', ');

                return (
                  <MenuItem key={cA.id} value={cA.id}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      alignItems="center"
                    >
                      <Typography {...typographyWrap}>
                        {addressLabel}
                      </Typography>

                      <Tooltip placement="right" title={title} arrow>
                        <Typography
                          variant="body2"
                          sx={{ ml: 1, cursor: 'help', fontWeight: 'bold' }}
                        >
                          {letter}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tAddI('Customer Deposit')}
              name="customerDeposit"
              disabled={viewMode}
              type="number"
              error={Boolean(getFormikField(formik, 'customerDeposit').error)}
              helperText={getFormikField(formik, 'customerDeposit').error}
              value={getFormikField(formik, 'customerDeposit').value}
              onChange={formik.handleChange}
              onFocus={onNumberFieldFocus(formik, 'customerDeposit')}
              onBlur={onNumberFieldBlur(formik, 'customerDeposit')}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tAddI('Deposit Reference')}
              name="depositReference"
              disabled={viewMode}
              error={Boolean(getFormikField(formik, 'depositReference').error)}
              helperText={getFormikField(formik, 'depositReference').error}
              value={getFormikField(formik, 'depositReference').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tAddI('Payment Method')}
              select
              name="paymentTerms"
              disabled={viewMode}
              error={Boolean(getFormikField(formik, 'paymentTerms').error)}
              helperText={getFormikField(formik, 'paymentTerms').error}
              value={
                Array.isArray(selectedPaymentTerm) &&
                selectedPaymentTerm.length > 0
                  ? selectedPaymentTerm[0].PaymentTermId
                  : ''
              }
              onChange={(event) => {
                const selectedTerm = paymentTerms.find(
                  (pT) => pT.PaymentTermId === event.target.value,
                );
                formik.setFieldValue(
                  'paymentTerms',
                  selectedTerm ? [selectedTerm] : [],
                );
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {paymentTerms.map((pT) => {
                return (
                  <MenuItem key={pT.PaymentTermId} value={pT.PaymentTermId}>
                    {pT.name}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid {...gridItemProps}>
            <Box
              width={'100%'}
              maxWidth={'300px'}
              sx={{ display: 'flex', justifyContent: 'start' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={getFormikField(formik, 'applyServiceCharge').value}
                  />
                }
                label={tAddI('Apply Service Charge')}
                name="applyServiceCharge"
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
          <Grid {...gridItemProps}>
            <Box
              width={'100%'}
              maxWidth={'300px'}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <TextField
                variant="filled"
                disabled={!getFormikField(formik, 'applyServiceCharge').value}
                type="number"
                fullWidth
                sx={{ maxWidth: '100%', mr: 1 }}
                label={tAddI('Monthly Service Charge')}
                name="monthlyServiceCharge"
                inputProps={{ min: 0, max: 100 }}
                onFocus={onNumberFieldFocus(formik, 'monthlyServiceCharge')}
                onBlur={onNumberFieldBlur(formik, 'monthlyServiceCharge')}
                error={Boolean(
                  getFormikField(formik, 'monthlyServiceCharge').error,
                )}
                helperText={
                  getFormikField(formik, 'monthlyServiceCharge').error
                }
                value={getFormikField(formik, 'monthlyServiceCharge').value}
                onChange={formik.handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
              <TextField
                variant="filled"
                disabled={!getFormikField(formik, 'applyServiceCharge').value}
                type="number"
                fullWidth
                sx={{ maxWidth: '100%' }}
                label={tAddI('Grace Period (days)')}
                name="gracePeriod"
                onFocus={onNumberFieldFocus(formik, 'gracePeriod')}
                onBlur={onNumberFieldBlur(formik, 'gracePeriod')}
                error={Boolean(getFormikField(formik, 'gracePeriod').error)}
                helperText={getFormikField(formik, 'gracePeriod').error}
                value={getFormikField(formik, 'gracePeriod').value}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddI('Ship Date')}
                {...textFieldProps}
                value={estimatedShipDate}
                onChange={handleChangeEstShipDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(
                      getFormikField(formik, 'estimatedShipDate').error,
                    ),
                    helperText: getFormikField(formik, 'estimatedShipDate')
                      .error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tAddI('Ship Method')}
              select
              name="shipMethod"
              disabled={viewMode}
              error={Boolean(getFormikField(formik, 'shipMethod').error)}
              helperText={getFormikField(formik, 'shipMethod').error}
              value={selectedShipMethod}
              onChange={(event) => {
                const selectedSM = shipMethods.find(
                  (sM) => sM.ShipMethodId === event.target.value,
                );
                formik.setFieldValue(
                  'shipMethod',
                  selectedSM ? selectedSM.ShipMethodId : '',
                );
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {shipMethods.map((sM) => {
                return (
                  <MenuItem key={sM.ShipMethodId} value={sM.ShipMethodId}>
                    {sM.method}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddI('Deadline')}
                {...textFieldProps}
                value={deadline}
                onChange={handleChangeDeadline}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(getFormikField(formik, 'deadline').error),
                    helperText: getFormikField(formik, 'deadline').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                disabled={viewMode}
                variant="filled"
                label={tAddI('Invoice Memos')}
                multiline
                rows={4}
                fullWidth
                sx={{
                  backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
                }}
                name="invoiceMemos"
                error={Boolean(getFormikField(formik, 'invoiceMemos').error)}
                helperText={getFormikField(formik, 'invoiceMemos').error}
                value={getFormikField(formik, 'invoiceMemos').value}
                onChange={formik.handleChange}
              />
            </Box>
          </Grid>
        </Stack>
      </Stack>
    </>
  );
}
